import React, { useEffect, useRef, useState, useCallback } from "react";
import { Radio, Volume2, VolumeX, Activity, Waves, Antenna, ScanLine, X } from "lucide-react";

// ──────────────────────────────────────────────────────────────
// Space Noise Listener
// Web Audio API-based cosmic noise simulation tuned to the
// selected destination. Surfaces "received" radio + electromagnetic
// signals decoded into short text bursts as if captured by a deep
// space network receiver.
// ──────────────────────────────────────────────────────────────

const BAND_PRESETS = {
  radio: {
    label_en: "Radio · 1.4 GHz Hydrogen Line",
    label_pt: "Rádio · Linha do Hidrogênio 1,4 GHz",
    color: "#62FFB0",
    base_freq: 220,
    sweep_speed: 0.6,
    noise_gain: 0.18,
  },
  em: {
    label_en: "Electromagnetic · 100 MHz Radar",
    label_pt: "Eletromagnética · Radar 100 MHz",
    color: "#FFD27A",
    base_freq: 380,
    sweep_speed: 1.4,
    noise_gain: 0.12,
  },
  cosmic: {
    label_en: "Cosmic · Microwave Background",
    label_pt: "Cósmica · Radiação Cósmica de Fundo",
    color: "#A077FF",
    base_freq: 90,
    sweep_speed: 0.18,
    noise_gain: 0.28,
  },
};

// Deterministic "transmissions" snippets per destination
const DECODED_SNIPPETS = {
  // Solar System
  luna: [
    { en: "Apollo legacy beacon, channel 121.5 MHz, faint carrier detected.",
      pt: "Beacon legado Apollo, canal 121,5 MHz, portadora fraca detectada." },
    { en: "Lunar South Pole comms relay — packet drop 0.04%, nominal.",
      pt: "Relé de comunicações do Polo Sul Lunar — perda 0,04%, nominal." },
    { en: "LRO downlink fragment: regolith spectroscopy frame 0x4E2A.",
      pt: "Fragmento downlink LRO: espectroscopia de regolito frame 0x4E2A." },
  ],
  mars: [
    { en: "MAVEN orbiter telemetry: solar wind 412 km/s, dust opacity 0.81.",
      pt: "Telemetria orbitador MAVEN: vento solar 412 km/s, opacidade poeira 0,81." },
    { en: "Perseverance rover EM whisper · drill depth 6.8 cm · cache full.",
      pt: "Sussurro EM do rover Perseverance · perfuração 6,8 cm · cache cheio." },
    { en: "Helicopter Ingenuity ping — last flight log fragment recovered.",
      pt: "Ping do helicóptero Ingenuity — fragmento de log de voo recuperado." },
  ],
  voyager1: [
    { en: "Voyager 1 carrier 8.4 GHz — drift +0.0014 Hz, plasma density rising.",
      pt: "Portadora Voyager 1 em 8,4 GHz — drift +0,0014 Hz, densidade de plasma subindo." },
    { en: "Cosmic ray subsystem CRS — 4 events/min, interstellar medium confirmed.",
      pt: "Subsistema de raios cósmicos CRS — 4 eventos/min, meio interestelar confirmado." },
  ],
  "proxima-b": [
    { en: "Proxima Centauri stellar flare detected — type M5.5Ve, peak 12s ago.",
      pt: "Erupção estelar Proxima Centauri detectada — tipo M5.5Ve, pico há 12s." },
    { en: "Habitable zone radio survey — no artificial signature, only stellar hiss.",
      pt: "Varredura rádio de zona habitável — nenhuma assinatura artificial, apenas chiado estelar." },
  ],
  "trappist-1e": [
    { en: "TRAPPIST-1 photometric dip 0.07% — possible transit alignment 4.05d cycle.",
      pt: "Queda fotométrica TRAPPIST-1 de 0,07% — possível trânsito alinhado ciclo 4,05d." },
    { en: "Atmospheric methane absorption ambiguous — JWST overlay queued.",
      pt: "Absorção de metano atmosférico ambígua — overlay JWST na fila." },
  ],
  // Generic fallback
  _default: [
    { en: "Deep-space carrier wave — 18 dB above noise floor, narrow band.",
      pt: "Onda portadora do espaço profundo — 18 dB acima do piso de ruído, banda estreita." },
    { en: "Pulsed electromagnetic burst · period 5.7s · likely natural rotator.",
      pt: "Pulso eletromagnético · período 5,7s · provável rotator natural." },
    { en: "Cosmic background hiss 2.725 K · isotropic to 1 part in 10⁵.",
      pt: "Chiado de fundo cósmico 2,725 K · isotrópico a 1 parte em 10⁵." },
    { en: "Faint coherent ping — origin unresolved, archived for review.",
      pt: "Ping coerente fraco — origem não resolvida, arquivado para revisão." },
    { en: "Plasma density spike — solar wind interaction with magnetopause.",
      pt: "Pico de densidade de plasma — interação vento solar com magnetopausa." },
  ],
};

function snippetsFor(destinationId) {
  return DECODED_SNIPPETS[destinationId] || DECODED_SNIPPETS._default;
}

export default function SpaceNoiseListener({ destination, lang = "en", onClose, compact = false }) {
  const [active, setActive] = useState(false);
  const [band, setBand] = useState("radio");
  const [muted, setMuted] = useState(false);
  const [volume, setVolume] = useState(0.45);
  const [received, setReceived] = useState([]);
  const [snrDb, setSnrDb] = useState(0);

  const audioCtxRef = useRef(null);
  const masterGainRef = useRef(null);
  const noiseNodeRef = useRef(null);
  const oscRef = useRef(null);
  const lfoRef = useRef(null);
  const analyserRef = useRef(null);
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const decodeIntervalRef = useRef(null);

  const preset = BAND_PRESETS[band];
  const destId = destination?.id || "_default";
  const destName = destination?.name || "Free Space";

  // Build a deterministic seed so same destination behaves consistently
  const seedHash = useRef(0);
  useEffect(() => {
    let h = 2166136261;
    for (const c of destId + band) h = ((h ^ c.charCodeAt(0)) * 16777619) >>> 0;
    seedHash.current = h;
  }, [destId, band]);

  const stop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    if (decodeIntervalRef.current) clearInterval(decodeIntervalRef.current);
    try { oscRef.current?.stop(); } catch (_e) { /* node already stopped */ }
    try { lfoRef.current?.stop(); } catch (_e) { /* node already stopped */ }
    try { noiseNodeRef.current?.stop?.(); } catch (_e) { /* node already stopped */ }
    try { audioCtxRef.current?.close(); } catch (_e) { /* context already closed */ }
    audioCtxRef.current = null;
    masterGainRef.current = null;
    noiseNodeRef.current = null;
    oscRef.current = null;
    lfoRef.current = null;
    analyserRef.current = null;
  }, []);

  const start = useCallback(async () => {
    // Lazily create AudioContext (must be in response to user gesture)
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    audioCtxRef.current = ctx;

    // Master gain
    const master = ctx.createGain();
    master.gain.value = muted ? 0 : volume * preset.noise_gain * 1.6;
    master.connect(ctx.destination);
    masterGainRef.current = master;

    // White-noise buffer source (2s loop)
    const bufSize = 2 * ctx.sampleRate;
    const noiseBuf = ctx.createBuffer(1, bufSize, ctx.sampleRate);
    const ch = noiseBuf.getChannelData(0);
    // Lightly colored noise: pink-ish for cosmic, white for radio, blue-ish for EM
    let lastValue = 0;
    for (let i = 0; i < bufSize; i++) {
      const white = Math.random() * 2 - 1;
      if (band === "cosmic") {
        lastValue = lastValue * 0.94 + white * 0.06; // low-pass
        ch[i] = lastValue * 4;
      } else if (band === "em") {
        ch[i] = (white - lastValue) * 0.9;
        lastValue = white;
      } else {
        ch[i] = white;
      }
    }
    const noise = ctx.createBufferSource();
    noise.buffer = noiseBuf;
    noise.loop = true;
    noiseNodeRef.current = noise;

    // Bandpass filter to color the noise
    const bp = ctx.createBiquadFilter();
    bp.type = "bandpass";
    bp.frequency.value = preset.base_freq * 3;
    bp.Q.value = band === "em" ? 12 : (band === "radio" ? 6 : 1.4);
    noise.connect(bp);

    // Slow LFO modulates the bandpass center frequency (sweep effect)
    const lfo = ctx.createOscillator();
    lfo.frequency.value = preset.sweep_speed;
    const lfoGain = ctx.createGain();
    lfoGain.gain.value = preset.base_freq * 1.5;
    lfo.connect(lfoGain).connect(bp.frequency);
    lfo.start();
    lfoRef.current = lfo;

    // Main carrier oscillator (subtle warble)
    const osc = ctx.createOscillator();
    osc.type = band === "radio" ? "sine" : "triangle";
    osc.frequency.value = preset.base_freq;
    const oscGain = ctx.createGain();
    oscGain.gain.value = 0.045;
    osc.connect(oscGain).connect(master);
    osc.start();
    oscRef.current = osc;

    // Analyser for visualization
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 256;
    bp.connect(analyser);
    analyser.connect(master);
    analyserRef.current = analyser;

    noise.start();

    // Drawing loop
    const buf = new Uint8Array(analyser.frequencyBinCount);
    const draw = () => {
      const canvas = canvasRef.current;
      if (canvas && analyser) {
        const c = canvas.getContext("2d");
        const w = canvas.width = canvas.offsetWidth;
        const h = canvas.height = canvas.offsetHeight;
        c.fillStyle = "#03040B";
        c.fillRect(0, 0, w, h);

        // Frequency bars
        analyser.getByteFrequencyData(buf);
        const barW = w / buf.length;
        let sum = 0;
        for (let i = 0; i < buf.length; i++) {
          const v = buf[i];
          sum += v;
          const bh = (v / 255) * h;
          const grad = c.createLinearGradient(0, h, 0, h - bh);
          grad.addColorStop(0, preset.color + "AA");
          grad.addColorStop(1, preset.color + "22");
          c.fillStyle = grad;
          c.fillRect(i * barW, h - bh, barW - 0.5, bh);
        }

        // Time-domain overlay (oscilloscope line)
        analyser.getByteTimeDomainData(buf);
        c.strokeStyle = "#00D4FF";
        c.lineWidth = 1.1;
        c.beginPath();
        for (let i = 0; i < buf.length; i++) {
          const x = (i / buf.length) * w;
          const y = (buf[i] / 255) * h;
          if (i === 0) c.moveTo(x, y); else c.lineTo(x, y);
        }
        c.stroke();

        // Crosshair
        c.strokeStyle = "#13243A";
        c.beginPath();
        c.moveTo(0, h / 2); c.lineTo(w, h / 2);
        c.moveTo(w / 2, 0); c.lineTo(w / 2, h);
        c.stroke();

        setSnrDb(Math.round((sum / buf.length - 40) * 0.8) - 30);
      }
      rafRef.current = requestAnimationFrame(draw);
    };
    draw();

    // Periodic "decoded transmissions"
    const pool = snippetsFor(destId);
    let counter = 0;
    decodeIntervalRef.current = setInterval(() => {
      const s = pool[(seedHash.current + counter) % pool.length];
      counter += 1;
      const stamp = new Date().toISOString().slice(11, 19);
      setReceived((prev) => [
        { id: `${Date.now()}-${counter}`, text: lang === "pt" ? s.pt : s.en, ts: stamp,
          freq: (preset.base_freq + (counter % 7) * 7).toFixed(2) },
        ...prev.slice(0, 7),
      ]);
    }, 4200);
  }, [band, destId, lang, muted, preset.base_freq, preset.color, preset.noise_gain, preset.sweep_speed, volume]);

  // Restart audio whenever band / volume / mute changes while active
  useEffect(() => {
    if (active) {
      stop();
      start();
    }
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [band, active]);

  useEffect(() => {
    if (masterGainRef.current) {
      masterGainRef.current.gain.value = muted ? 0 : volume * preset.noise_gain * 1.6;
    }
  }, [muted, volume, preset.noise_gain]);

  useEffect(() => () => stop(), [stop]);

  return (
    <div className={`holo-panel relative overflow-hidden ${compact ? "p-4" : "p-5"}`} data-testid="space-noise-listener"
         style={{ borderColor: `${preset.color}30` }}>
      <div className="absolute inset-x-0 top-0 h-px"
           style={{ background: `linear-gradient(90deg, transparent, ${preset.color}, transparent)` }} />

      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div className="flex-1 min-w-[180px]">
          <div className="label-overline flex items-center gap-2" style={{ color: preset.color }}>
            <Antenna size={12} /> {lang === "pt" ? "Escuta Espacial" : "Space Listener"}
          </div>
          <div className="font-display font-black text-xl tracking-tighter mt-1">
            {lang === "pt" ? "RUÍDO DE" : "NOISE FROM"} <span style={{ color: preset.color }}>{destName.toUpperCase()}</span>
          </div>
          <div className="font-mono-data text-[10px] tracking-[0.16em] uppercase text-white/55 mt-1">
            {lang === "pt" ? preset.label_pt : preset.label_en}  ·  SNR {snrDb} dB
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!active ? (
            <button onClick={() => setActive(true)}
                    className="btn-primary-s2s !text-[10px]"
                    style={{ borderColor: preset.color, color: preset.color }}
                    data-testid="space-noise-listen">
              <Radio size={12} /> {lang === "pt" ? "ESCUTAR" : "LISTEN"}
            </button>
          ) : (
            <button onClick={() => { setActive(false); stop(); }}
                    className="btn-secondary-s2s !text-[10px]"
                    data-testid="space-noise-stop">
              <X size={12} /> {lang === "pt" ? "PARAR" : "STOP"}
            </button>
          )}
          {onClose && (
            <button onClick={onClose} className="text-white/40 hover:text-white" data-testid="space-noise-close">
              <X size={14} />
            </button>
          )}
        </div>
      </div>

      {/* Band selector */}
      <div className="flex gap-2 mb-3 flex-wrap">
        {Object.entries(BAND_PRESETS).map(([k, p]) => (
          <button key={k} onClick={() => setBand(k)}
                  data-testid={`band-${k}`}
                  className="px-3 py-1.5 font-mono-data text-[10px] tracking-[0.18em] uppercase transition-all"
                  style={{
                    border: `1px solid ${band === k ? p.color : "#13243A"}`,
                    background: band === k ? `${p.color}15` : "transparent",
                    color: band === k ? p.color : "#9CB6CC",
                  }}>
            {k === "radio" ? (lang === "pt" ? "RÁDIO" : "RADIO")
              : k === "em" ? (lang === "pt" ? "ELETROMAG." : "EM RADAR")
              : (lang === "pt" ? "CÓSMICO" : "COSMIC")}
          </button>
        ))}
      </div>

      {/* Visualizer canvas */}
      <div className="border border-white/10 mb-3" style={{ height: compact ? 90 : 130 }}>
        <canvas ref={canvasRef} className="w-full h-full block" data-testid="space-noise-scope" />
      </div>

      {/* Volume / mute */}
      <div className="flex items-center gap-3 mb-3">
        <button onClick={() => setMuted((m) => !m)} className="text-white/70 hover:text-white"
                data-testid="space-noise-mute">
          {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
        </button>
        <input type="range" min="0" max="1" step="0.01" value={volume}
               onChange={(e) => setVolume(parseFloat(e.target.value))}
               className="flex-1 accent-[#00D4FF]"
               data-testid="space-noise-volume" />
        <span className="font-mono-data text-[10px] tracking-[0.16em] text-white/55 w-12 text-right">
          {Math.round(volume * 100)}%
        </span>
      </div>

      {/* Decoded transmissions feed */}
      <div className="border border-white/5 bg-black/40 p-3">
        <div className="label-overline flex items-center gap-2 mb-2">
          <ScanLine size={11} /> {lang === "pt"
            ? "TRANSMISSÕES DECODIFICADAS · RECEBIDAS"
            : "DECODED TRANSMISSIONS · RECEIVED"}
        </div>
        {received.length === 0 ? (
          <div className="font-mono-data text-[11px] text-white/35 italic" data-testid="space-noise-empty">
            {active
              ? (lang === "pt" ? "Aguardando sinais detectáveis…" : "Awaiting detectable signals…")
              : (lang === "pt" ? "Pressione ESCUTAR para captar transmissões." : "Press LISTEN to capture transmissions.")}
          </div>
        ) : (
          <div className="space-y-2 max-h-48 overflow-auto" data-testid="space-noise-feed">
            {received.map((r) => (
              <div key={r.id} className="border-l-2 pl-3 py-1" style={{ borderColor: preset.color }}>
                <div className="flex items-center justify-between gap-3">
                  <span className="font-mono-data text-[9px] tracking-[0.18em] text-white/55">
                    {r.ts} · {r.freq} Hz
                  </span>
                  <Waves size={9} style={{ color: preset.color }} />
                </div>
                <div className="font-mono-data text-[11px] text-white/85 mt-1 leading-relaxed">
                  ▸ {r.text}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="font-mono-data text-[9px] tracking-[0.18em] uppercase text-white/35 mt-3 flex items-center gap-2">
        <Activity size={10} /> {lang === "pt"
          ? "Simulação científica de ruído cósmico baseada em assinaturas reais de rádio + EM."
          : "Scientific simulation of cosmic noise based on real radio + EM signatures."}
      </div>
    </div>
  );
}
